import React, { ReactNode, useState } from "react";

interface DragSourceProps<T> {
  dragId: string[];
  data: T;
  children: ReactNode;
  onDragStart?: () => void;
  onDrop?: (data: T) => void;
  className?: string;
  block?: boolean;
}

export function DragSource<T>({
  dragId,
  data,
  children,
  onDragStart,
  onDrop,
  className = "",
  block = false,
}: DragSourceProps<T>) {
  const [isDragging, setIsDragging] = useState(false);
  const [isOver, setIsOver] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    try {
      setIsDragging(true);
      // Set drag data
      const jsonData = JSON.stringify(data);
      const jsonDragId = JSON.stringify(dragId);
      e.dataTransfer.setData("application/json", jsonData);
      e.dataTransfer.setData("application/drag-id", jsonDragId);
      e.dataTransfer.effectAllowed = "move";

      // Create a drag image
      const dragPreview = document.createElement('div');
      dragPreview.className = 'bg-white shadow-lg rounded-lg p-4 border border-blue-200';
      dragPreview.textContent = 'Moving module...';
      document.body.appendChild(dragPreview);
      e.dataTransfer.setDragImage(dragPreview, 0, 0);
      setTimeout(() => document.body.removeChild(dragPreview), 0);

      onDragStart?.();
    } catch (error) {
      console.error("Error starting drag:", error);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setIsOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!isOver) setIsOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsOver(false);
    if (!onDrop) return;

    try {
      const droppedData = JSON.parse(
        e.dataTransfer.getData("application/json")
      );
      const sourceDragId = JSON.parse(
        e.dataTransfer.getData("application/drag-id")
      );
      
      if (dragId.some((id: string) => sourceDragId.includes(id))) {
        onDrop(droppedData);
      }
    } catch (error) {
      console.error("Error processing drop:", error);
    }
  };

  const Element = block ? 'div' : 'span';
  
  return (
    <Element
      draggable
      className={`${className} ${isDragging ? 'opacity-50 scale-[0.98]' : ''} ${
        isOver ? 'border-t-2 border-blue-500' : ''
      } transition-all duration-200`}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
    </Element>
  );
}
